import type { ReactNode } from 'react';

import { Alert } from '~/components/ui';
import RichText from '~/components/pages/ai/placements/preview/RichText';
import TextArea from '~/components/pages/ai/placements/preview/TextArea';
import TextField, {
  type PlacementPreviewFieldProps,
  type PlacementRunAi,
} from '~/components/pages/ai/placements/preview/TextField';
import type {
  PlacementComponentOption,
  PlacementComponentVersionOption,
} from '~/modules/query/ai/types';

/**
 * The preview registry: which component renders each catalogue id, and how its sample
 * data becomes props. service-ai returns placements only, so the catalogue itself is
 * the static `PLACEMENT_COMPONENTS` list in `~/lib/ai/placementDefaults`.
 */
export interface PlacementComponentRenderProps {
  aiDisabled: boolean;
  aiDisableTooltip?: string;
  componentId: string;
  componentVersion: string;
  disabledTooltip?: string;
  isDisabled: boolean;
  isRunning: boolean;
  onRunAi?: PlacementRunAi;
  placementKey: string;
  sampleContext: Record<string, unknown>;
  sampleProps: Record<string, unknown>;
}

const getString = (
  source: Record<string, unknown> | undefined,
  key: string,
  fallback: string,
): string => {
  const value = source?.[key];

  return typeof value === 'string' && value.trim().length > 0 ? value : fallback;
};

const getBoolean = (
  source: Record<string, unknown> | undefined,
  key: string,
  fallback: boolean,
): boolean => {
  const value = source?.[key];

  return typeof value === 'boolean' ? value : fallback;
};

const parseBoolean = (source: Record<string, unknown>, key: string, fallback: boolean): boolean => {
  const value = source[key];

  if (typeof value === 'boolean') {
    return value;
  }

  return value === 'true' ? true : value === 'false' ? false : fallback;
};

/** A positive whole number from a JSON number or a numeric string; anything else is no limit. */
const parseCharacterLimit = (source: Record<string, unknown>): number | undefined => {
  const value = source.characterLimit;
  const limit = typeof value === 'string' ? Number(value.trim() || Number.NaN) : value;

  return typeof limit === 'number' && Number.isInteger(limit) && limit > 0 ? limit : undefined;
};

const optionalString = (source: Record<string, unknown>, key: string): string | undefined => {
  const value = source[key];

  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
};

/** The label, counter, disclaimer and insert-button props the text field and text area share. */
function resolveTextAiProps(
  sampleProps: Record<string, unknown>,
): Partial<PlacementPreviewFieldProps> {
  return {
    // An empty label is kept: it hides the label text.
    labelText: typeof sampleProps.labelText === 'string' ? sampleProps.labelText : undefined,
    labelTooltip: optionalString(sampleProps, 'labelTooltip'),
    required: parseBoolean(sampleProps, 'required', false),
    errorText: optionalString(sampleProps, 'errorText'),
    characterLimit: parseCharacterLimit(sampleProps),
    tipText: optionalString(sampleProps, 'tipText'),
    disclaimerText: optionalString(sampleProps, 'disclaimerText'),
    showDisclaimer: parseBoolean(sampleProps, 'showDisclaimer', true),
    bracesButton: parseBoolean(sampleProps, 'bracesButton', true),
    emojiButton: parseBoolean(sampleProps, 'emojiButton', true),
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

function resolveTextFieldProps(props: PlacementComponentRenderProps): PlacementPreviewFieldProps {
  const sampleProps = isRecord(props.sampleProps) ? props.sampleProps : {};

  return {
    defaultValue:
      typeof sampleProps.defaultValue === 'string'
        ? sampleProps.defaultValue
        : getString(props.sampleContext, 'initialText', ''),
    placeholder: getString(sampleProps, 'placeholder', 'Type your message…'),
    isDisabled: getBoolean(sampleProps, 'isDisabled', props.isDisabled),
    disabledTooltip: getString(sampleProps, 'disabledTooltip', props.disabledTooltip ?? ''),
    aiDisabled: props.aiDisabled || getBoolean(sampleProps, 'aiDisabled', false),
    aiDisableTooltip: getString(sampleProps, 'aiDisableTooltip', props.aiDisableTooltip ?? ''),
    isRunning: props.isRunning || getBoolean(sampleProps, 'isRunning', false),
    onRunAi: props.onRunAi,
    ...resolveTextAiProps(sampleProps),
  };
}

function resolveTextAreaProps(props: PlacementComponentRenderProps): PlacementPreviewFieldProps {
  const sampleProps = isRecord(props.sampleProps) ? props.sampleProps : {};

  return {
    defaultValue: getString(props.sampleContext, 'initialText', ''),
    placeholder: getString(sampleProps, 'placeholder', 'Type your message…'),
    isDisabled: props.isDisabled,
    disabledTooltip: props.disabledTooltip,
    aiDisabled: props.aiDisabled,
    aiDisableTooltip: props.aiDisableTooltip,
    isRunning: props.isRunning,
    onRunAi: props.onRunAi,
    ...resolveTextAiProps(sampleProps),
  };
}

function resolveRichTextProps(props: PlacementComponentRenderProps): PlacementPreviewFieldProps {
  const sampleProps = isRecord(props.sampleProps) ? props.sampleProps : {};

  return {
    defaultValue: getString(
      props.sampleContext,
      'initialHtml',
      getString(props.sampleContext, 'initialText', ''),
    ),
    placeholder: getString(sampleProps, 'placeholder', 'Write your rich text content…'),
    isDisabled: props.isDisabled,
    disabledTooltip: props.disabledTooltip,
    aiDisabled: props.aiDisabled,
    aiDisableTooltip: props.aiDisableTooltip,
    isRunning: props.isRunning,
    onRunAi: props.onRunAi,
  };
}

const RENDERERS: Record<
  string,
  {
    Component: (props: PlacementPreviewFieldProps) => ReactNode;
    resolveProps: (props: PlacementComponentRenderProps) => PlacementPreviewFieldProps;
    title: string;
  }
> = {
  'text.field': { title: 'Text field', Component: TextField, resolveProps: resolveTextFieldProps },
  'text.area': { title: 'Text area', Component: TextArea, resolveProps: resolveTextAreaProps },
  'rich.text': { title: 'Rich text', Component: RichText, resolveProps: resolveRichTextProps },
};

export function resolveComponentVersionOption(
  components: PlacementComponentOption[],
  componentId: string,
  componentVersion: string,
): PlacementComponentVersionOption | null {
  const component = components.find((entry) => entry.id === componentId);

  return component?.versions.find((entry) => entry.version === componentVersion) ?? null;
}

export function buildSnippetFromComponentVersion(
  componentVersion: PlacementComponentVersionOption,
  placementKey: string,
): string {
  return componentVersion.renderSnippet
    .replace(/\{\{placementKey\}\}/g, placementKey)
    .replace(/\{\{componentVersion\}\}/g, componentVersion.version);
}

/** The live preview for a placement's component, framed with the key and version it renders. */
export default function renderPlacementComponentPreview(props: PlacementComponentRenderProps) {
  const renderer = RENDERERS[props.componentId];

  if (!renderer) {
    return (
      <Alert variant="error">
        <p className="text-sm text-gray-800 dark:text-white/90">
          This placement uses {props.componentId || 'an unknown component'} {props.componentVersion}
          , which has no preview. Choose a component from the catalogue.
        </p>
      </Alert>
    );
  }

  const Component = renderer.Component;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800">
      <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-800">
        <p className="text-sm font-medium text-gray-800 dark:text-white/90">{renderer.title}</p>
        <p className="font-mono text-xs text-gray-500 dark:text-gray-400">
          {props.placementKey} @ {props.componentVersion}
        </p>
      </div>
      <div className="p-4">
        <Component {...renderer.resolveProps(props)} />
      </div>
    </div>
  );
}
